/**
 * Which steps each profession's applicant actually walks.
 *
 * SEPARATE FROM professional-invite-steps.ts to break a cycle, and the cycle
 * was the useful part: NOTARY_FLOW must be DERIVED from NOTARY_STEPS rather
 * than restating it. A second array repeating those labels would be two
 * sources of truth about one wizard, and the C-3 test — which compares the
 * composition against the flow — could not tell they had drifted, because both
 * would be its own constructs. The generic machinery lives there, the notary's
 * gated steps live in notary-invite-steps.ts, and this module is the only
 * place that knows both.
 */
import { composeSteps, type ComposedStep } from "@/pages/auth/professional-invite-steps";
import { NOTARY_STEPS } from "@/pages/auth/notary-invite-steps";

/**
 * The notary flow, derived. Its bundling — a step per concern — is preserved
 * exactly, because this IS that list.
 */
export const NOTARY_FLOW: readonly ComposedStep[] = NOTARY_STEPS.map((definition) => ({
  concerns: [definition.concern],
  labelKey: definition.labelKey,
  requirements: definition.requirements,
}));

/**
 * The accountant flow — the SAME signup tail every profession walks, with
 * this discipline's two verification steps in front of it.
 *
 * IT USED TO BUNDLE. Phone, profile, coverage and password were one step
 * called "Tu perfil", and slice 2 preserved that bundling on the grounds
 * that it was the flow people were completing. The product decision is that
 * professions differ ONLY in the verification steps their discipline
 * requires — the tail is shared — so the bundling had no reason left and is
 * gone. This is the spec's RES-1 settled in option B's direction for the
 * tail, on an explicit call rather than by a refactor's drift.
 *
 * THE TAIL'S LABELS ARE THE SHARED ONES, and the two verification steps keep
 * this profession's. That split is the whole principle in one line: a step
 * that exists because of a discipline is named by it, and a step every
 * professional walks is named once.
 *
 * It is written out rather than derived because there is no accountant
 * equivalent of NOTARY_STEPS to derive from, and inventing one to satisfy a
 * symmetry would be ceremony. C-1 holds its requirements against the
 * strategy's declaration, which is the part that can silently go wrong.
 */
export const ACCOUNTANT_FLOW: readonly ComposedStep[] = [
  {
    concerns: ["identity"],
    labelKey: "auth.acceptAccountantInvitation.stepIdentity",
    requirements: ["identity.cedula", "identity.names"],
  },
  {
    concerns: ["certificate"],
    labelKey: "auth.acceptAccountantInvitation.stepCertificate",
    requirements: ["document.certificate"],
  },
  { concerns: ["phone"], labelKey: "auth.acceptProfessionalInvitation.step.phone", requirements: [] },
  { concerns: ["profile"], labelKey: "auth.acceptProfessionalInvitation.step.profile", requirements: [] },
  { concerns: ["coverage"], labelKey: "auth.acceptProfessionalInvitation.step.coverage", requirements: [] },
  { concerns: ["billing"], labelKey: "auth.acceptProfessionalInvitation.step.billing", requirements: [] },
  { concerns: ["password"], labelKey: "auth.acceptProfessionalInvitation.step.password", requirements: [] },
];

/**
 * The flow a single strategy walks today, or undefined when it has none.
 *
 * `manual_review` deliberately returns undefined: it is new, nobody is
 * completing it, so it takes the canonical sequence rather than inheriting a
 * bundling chosen for a different profession.
 */
export function flowForStrategy(strategy: string): readonly ComposedStep[] | undefined {
  if (strategy === "poder_judicial") return NOTARY_FLOW;
  if (strategy === "document_lookup") return ACCOUNTANT_FLOW;
  return undefined;
}

/** `composeSteps` with the real flows bound. */
export function stepsForStrategies(strategyNames: readonly string[]): ComposedStep[] {
  return composeSteps(strategyNames, flowForStrategy);
}
